/**
 * Event code generation and normalization.
 *
 * New codes are drawn from an unambiguous alphabet so codes can be read aloud
 * or copied from a screen without O/0 or I/1/l confusion.
 *
 * Legacy codes were generated from base36 and may contain excluded characters.
 * Normalization therefore never substitutes characters: it only trims, upcases,
 * and strips separators, so an old code still resolves to the same document id.
 */

export const EVENT_CODE_LENGTH = 6;

/** Excludes 0, 1, O, I and L. */
export const EVENT_CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';

const AMBIGUOUS_CHARACTERS = ['0', '1', 'O', 'I', 'L'];

function getCryptoObject() {
  return typeof window !== 'undefined' ? window.crypto : undefined;
}

function randomIndexes(count, max) {
  const cryptoObj = getCryptoObject();

  if (cryptoObj?.getRandomValues) {
    const bytes = new Uint32Array(count);
    cryptoObj.getRandomValues(bytes);
    // Rejection-free modulo is unnecessary here; the bias over 2^32 is negligible.
    return Array.from(bytes, (value) => value % max);
  }

  return Array.from({ length: count }, () => Math.floor(Math.random() * max));
}

export function generateEventCode(length = EVENT_CODE_LENGTH) {
  const size = Number.isInteger(length) && length > 0 ? length : EVENT_CODE_LENGTH;
  return randomIndexes(size, EVENT_CODE_ALPHABET.length)
    .map((index) => EVENT_CODE_ALPHABET[index])
    .join('');
}

/**
 * Prepare user input for a document lookup. Characters are preserved exactly so
 * legacy codes containing 0/1/O/I/L keep working.
 */
export function normalizeEventCode(input) {
  if (typeof input !== 'string') return '';
  return input.replace(/[\s-]/g, '').toUpperCase();
}

export function isValidEventCodeShape(input) {
  const normalized = normalizeEventCode(input);
  return /^[A-Z0-9]{4,12}$/.test(normalized);
}

/** True when every character comes from the new unambiguous alphabet. */
export function isUnambiguousEventCode(input) {
  const normalized = normalizeEventCode(input);
  if (!normalized) return false;
  return normalized.split('').every((char) => EVENT_CODE_ALPHABET.includes(char));
}

export function containsAmbiguousCharacters(input) {
  const normalized = normalizeEventCode(input);
  return AMBIGUOUS_CHARACTERS.some((char) => normalized.includes(char));
}
